import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import FuseDialog from '@fuse/core/FuseDialog';
import useShowMessage from 'app/hooks/useShowMessage';
import { MESSAGE_TYPE } from 'app/constants/message';
import { ControlledTextField, ControlledSelect, ControlledCheckbox, Tooltip } from 'app/shared-components';
import { closeDialog, selectOpenedDialog, selectDialogOptions } from 'app/store/fuse/dialogSlice';
import yup from 'app/helpers/validation';
import DIALOGS from 'app/constants/dialogs';
import { countries } from 'app/constants/countries';
import { EU_COUNTRY_CODES } from 'app/constants/euCountries';
import { TRANSLATION_KEY } from '../constants';
import { actions, selectors, STATUS } from '../store/billingSlice';

const defaultValues = {
  countryCode: '',
  vatNumber: '',
  email: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  phoneNumber: '',
  signedAgreement: false,
};

const BillingDetailsDialog = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(TRANSLATION_KEY);
  const showMessage = useShowMessage();
  const billingProfile = useSelector(selectors.selectBillingProfile);
  const createStatus = useSelector(selectors.selectCreateBillingProfileStatus);
  const isLoading = createStatus === STATUS.PENDING;
  const isOpen = useSelector(selectOpenedDialog) === DIALOGS.BILLING_DETAILS;
  const dialogOptions = useSelector(selectDialogOptions);
  const hasPlanContext = Boolean(dialogOptions?.planId && dialogOptions?.variantId);
  const [error, setError] = useState(null);

  const countryOptions = useMemo(
    () => Object.entries(countries).map(([value, label]) => ({ value, label })),
    []
  );

  const schema = useMemo(
    () =>
      yup.object().shape({
        countryCode: yup.string().required(t('COUNTRY_REQUIRED')),
        vatNumber: yup
          .string()
          .trim()
          .when('countryCode', {
            is: (code) => EU_COUNTRY_CODES.has(code),
            then: (s) =>
              s
                .required(t('VAT_NUMBER_REQUIRED'))
                .min(4, t('VAT_NUMBER_INVALID'))
                .max(20, t('VAT_NUMBER_INVALID')),
          }),
        email: yup
          .string()
          .trim()
          .required(t('BILLING_EMAIL_REQUIRED'))
          .email(t('BILLING_EMAIL_INVALID'))
          .max(254, t('BILLING_EMAIL_INVALID')),
        addressLine1: yup.string().trim().required(t('ADDRESS_LINE1_REQUIRED')).max(200, t('FIELD_TOO_LONG')),
        addressLine2: yup.string().trim().max(200, t('FIELD_TOO_LONG')),
        city: yup.string().trim().required(t('CITY_REQUIRED')).max(100, t('FIELD_TOO_LONG')),
        state: yup.string().trim().max(100, t('FIELD_TOO_LONG')),
        postalCode: yup.string().trim().required(t('POSTAL_CODE_REQUIRED')).max(20, t('FIELD_TOO_LONG')),
        phoneNumber: yup
          .string()
          .trim()
          .required(t('PHONE_NUMBER_REQUIRED'))
          .checkPhoneMethod('', t('PHONE_NUMBER_INVALID')),
        signedAgreement: yup.boolean(),
      }),
    [t]
  );

  const { control, handleSubmit, formState, reset, watch, setValue } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { isValid, dirtyFields } = formState;

  const selectedCountry = watch('countryCode');
  const isEuCountry = EU_COUNTRY_CODES.has(selectedCountry);

  const onClose = useCallback(() => {
    dispatch(closeDialog());
    setTimeout(() => setError(null), 300);
  }, [dispatch]);

  const onSubmit = handleSubmit(({ signedAgreement, ...data }) => {
    dispatch(actions.createBillingProfile(data))
      .unwrap()
      .then(() => {
        onClose();
        onSuccess?.();
      })
      .catch((e) => {
        const message = e?.message || t('SOMETHING_WENT_WRONG');
        setError(message);
        showMessage(message, MESSAGE_TYPE.ERROR);
      });
  });

  // Re-sync the form from the stored profile on every open — not only when
  // `billingProfile` changes — so reopening after a submit (which leaves the
  // profile reference unchanged) still prefills the fields.
  useEffect(() => {
    if (isOpen) {
      reset(billingProfile ? { ...defaultValues, ...billingProfile } : defaultValues);
    }
  }, [isOpen, billingProfile, reset]);

  useEffect(() => {
    if (!isEuCountry) {
      setValue('vatNumber', '', { shouldValidate: true });
    }
  }, [isEuCountry, setValue]);

  return (
    <FuseDialog
      fullWidth
      maxWidth="sm"
      dialogId={DIALOGS.BILLING_DETAILS}
      onClose={onClose}
      PaperProps={{ sx: { maxWidth: '560px' } }}
    >
      <Box padding="32px">
        <Typography variant="h5" fontWeight={700} color="accent.900">
          {t('BILLING_DETAILS_TITLE')}
        </Typography>
        <Typography variant="body2" color="accent.500" mt="8px">
          {t('BILLING_DETAILS_DESCRIPTION')}
        </Typography>

        <form onSubmit={onSubmit}>
          <Box display="flex" flexDirection="column" gap="20px" mt="24px">
            <ControlledSelect
              name="countryCode"
              control={control}
              label={t('COUNTRY_OF_REGISTRATION')}
              placeholder={t('SELECT_COUNTRY')}
              options={countryOptions}
              required
              defaultValue=""
            />

            {isEuCountry && (
              <Box>
                <ControlledTextField
                  name="vatNumber"
                  control={control}
                  label={t('VAT_NUMBER')}
                  placeholder={t('VAT_NUMBER_PLACEHOLDER')}
                  required
                />
                <Typography variant="caption" color="accent.400" mt="4px">
                  {t('VAT_NUMBER_HINT')}
                </Typography>
              </Box>
            )}

            <Box>
              <ControlledTextField
                name="email"
                control={control}
                label={t('BILLING_EMAIL')}
                placeholder={t('BILLING_EMAIL_PLACEHOLDER')}
                required
              />
              <Typography variant="caption" color="accent.400" mt="4px">
                {t('BILLING_EMAIL_HINT')}
              </Typography>
            </Box>

            <ControlledTextField
              name="addressLine1"
              control={control}
              label={t('ADDRESS_LINE1')}
              placeholder={t('ADDRESS_LINE1_PLACEHOLDER')}
              required
            />

            <ControlledTextField
              name="addressLine2"
              control={control}
              label={t('ADDRESS_LINE2')}
              placeholder={t('ADDRESS_LINE2_PLACEHOLDER')}
            />

            <Box display="flex" gap="16px">
              <ControlledTextField
                name="city"
                control={control}
                label={t('CITY')}
                placeholder={t('CITY_PLACEHOLDER')}
                required
              />
              <ControlledTextField
                name="state"
                control={control}
                label={t('STATE')}
                placeholder={t('STATE_PLACEHOLDER')}
              />
            </Box>

            <Box display="flex" gap="16px">
              <ControlledTextField
                name="postalCode"
                control={control}
                label={t('POSTAL_CODE')}
                placeholder={t('POSTAL_CODE_PLACEHOLDER')}
                required
              />
              <ControlledTextField
                name="phoneNumber"
                control={control}
                label={t('PHONE_NUMBER')}
                placeholder={t('PHONE_NUMBER_PLACEHOLDER')}
                required
              />
            </Box>

            {hasPlanContext && (
              <ControlledCheckbox
                name="signedAgreement"
                control={control}
                customLabel={
                  <Box display="flex" alignItems="center" gap="4px">
                    <Typography variant="body2">{t('SIGNED_AGREEMENT')}</Typography>
                    <Tooltip
                      text={t('SIGNED_AGREEMENT_TOOLTIP')}
                      icon="heroicons-outline:information-circle"
                      iconColor="accent.400"
                      iconSize={16}
                    />
                  </Box>
                }
              />
            )}

            {error && (
              <Typography variant="body2" color="error.main">
                {error}
              </Typography>
            )}

            <Box display="flex" gap="16px" mt="8px">
              <Button onClick={onClose} sx={{ textTransform: 'none', color: 'accent.900' }}>
                {t('BACK')}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                type="submit"
                disabled={!isValid || !Object.keys(dirtyFields).length || isLoading}
                fullWidth
                sx={{ borderRadius: '24px', textTransform: 'none' }}
                startIcon={isLoading && <CircularProgress size={16} color="inherit" />}
              >
                {hasPlanContext ? t('CONTINUE_TO_PAYMENT') : t('SAVE')}
              </Button>
            </Box>
          </Box>
        </form>
      </Box>
    </FuseDialog>
  );
};

export default BillingDetailsDialog;
